import React from "react";
import {
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format, parseISO, isValid } from "date-fns";

import {
  H6HeadlineSmall,
  BodyMedium,
  LabelLarge,
  H8TitleMedium
} from "../StyledComponents/Typography/Typography.tsx";
import { StyledIconButton } from "../StyledComponents/Buttons/AuradineButtons.js";
import { StyledDialog } from "../StyledComponents/Dialog/Dialog.js";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const displayTS = (dateStr) => {
  const parsed = parseISO(dateStr);
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy, h:mm a") : "";
};

const ActiveCurtailmentHeader = ({ isLoading }) => {
  const { activeSiteCurtailCount, setActiveSiteCurtailOpen } =
    useAppContextProvider();
  const { colors } = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      padding="1.5rem 0"
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <H6HeadlineSmall>Active Power Curtailment Sites</H6HeadlineSmall>
        {!isLoading && activeSiteCurtailCount !== null && (
          <BodyMedium>
            Curtailment active at {activeSiteCurtailCount} site
            {activeSiteCurtailCount === 1 ? "" : "s"}.
          </BodyMedium>
        )}
      </Box>

      <StyledIconButton colors={colors} onClick={() => setActiveSiteCurtailOpen(false)}>
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const bannerStyles = (colors) => ({
  tableWrapper: {
    borderRadius: "0.75rem",
    backgroundColor: colors?.background?.accent, 
    overflow: "hidden",
  },
  tableContainer: {
    maxHeight: 400,
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#ccc transparent",
  },
  tableCellHeader: {
    backgroundColor: colors?.background?.accent,
    position: "sticky",
    top: 0,
    zIndex: 2,
    padding: "1rem",
    borderBottom: `1px solid ${colors?.border?.card}`,
    color: colors?.text?.primary,
  },
  tableCellBody: {
    backgroundColor: colors?.background?.accent,
    borderBottom: `1px solid ${colors?.border?.card}`,
    color: colors?.text?.muted,
    padding: "1rem",
  },
  tableRow: {
    "&:last-of-type td": {
      borderBottom: "none",
    },
  },
});



export default function ActiveSiteCurtailmentDialog({
  activeSiteData,
  isLoading,
}) {
  const { setActiveSiteCurtailOpen, activeSiteCurtailOpen } =
    useAppContextProvider();
  const { colors } = useTheme();
  return (
    <StyledDialog
      colors={colors}
      maxWidth="md"
      fullWidth
      open={activeSiteCurtailOpen}
      hideBackdrop
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          setActiveSiteCurtailOpen(false);
        }
      }}
    >
      <ActiveCurtailmentHeader isLoading={isLoading} />
      <DialogContent>
        <Box sx={bannerStyles(colors).tableWrapper}>
          <Box sx={bannerStyles(colors).tableContainer}>
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={bannerStyles(colors).tableCellHeader}>
                    <H8TitleMedium>Site</H8TitleMedium>
                  </TableCell>
                  <TableCell sx={bannerStyles(colors).tableCellHeader}>
                    <H8TitleMedium>Ends On</H8TitleMedium>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? Array.from({ length: 3 }).map((_, index) => (
                      <TableRow key={`skeleton-${index}`} sx={bannerStyles(colors).tableRow}>
                        <TableCell sx={bannerStyles(colors).tableCellBody}>
                          <Skeleton variant="text" width="60%" height={24} />
                        </TableCell>
                        <TableCell sx={bannerStyles(colors).tableCellBody}>
                          <Skeleton variant="text" width="40%" height={24} />
                        </TableCell>
                      </TableRow>
                    ))
                  : activeSiteData.map(({ siteName, end_time }, index) => (
                      <TableRow key={`${siteName}-${index}`} sx={bannerStyles(colors).tableRow}>
                        <TableCell sx={bannerStyles(colors).tableCellBody}>
                          <BodyMedium>{siteName}</BodyMedium>
                        </TableCell>
                        <TableCell sx={bannerStyles(colors).tableCellBody}>
                          <BodyMedium>{displayTS(end_time)}</BodyMedium>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
}
